import { useSyncExternalStore } from "react";
import { scopeStorageKey } from "../../../../shared/types/accessScope";
import { ALL, readStoredScope, updateStoredScope } from "./scopeFilterState";

/**
 * The header's Location choice, one per instance, shared by every page (D27).
 *
 * Kept outside Redux on purpose: it is a viewer convenience persisted to this
 * browser (`skyhire:scope:<instanceId>`), and each page reads it through
 * `useStoredLocation`. The raw value is kept as stored; whether it is still a
 * choice this person may make is decided against their scope where it is read
 * (`validLocation`), so a stale value falls back to "all" without a message.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
/** instanceId → the remembered raw value (null = nothing remembered). */
const cache = new Map<string, string | null>();

/** `window.localStorage`, or nothing where reading the property itself throws. */
export function browserStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function notify() {
  listeners.forEach((listener) => listener());
}

/** Another tab changed the choice: forget ours so the next read picks theirs up. */
function onStorage(event: StorageEvent) {
  const prefix = scopeStorageKey("");
  if (event.key === null) {
    cache.clear();
    notify();
    return;
  }
  if (!event.key.startsWith(prefix)) return;
  cache.delete(event.key.slice(prefix.length));
  notify();
}

function subscribe(listener: Listener): () => void {
  if (listeners.size === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/** The remembered value for this instance, as stored. */
export function readLocationChoice(instanceId: string): string | null {
  if (!instanceId) return null;
  if (!cache.has(instanceId)) {
    cache.set(instanceId, readStoredScope(instanceId, browserStorage()).loc ?? null);
  }
  return cache.get(instanceId) ?? null;
}

/**
 * Remember a choice for this instance and tell every reader. "all" forgets it.
 * Storage failing (private mode, quota) still changes the choice for this tab.
 */
export function writeLocationChoice(instanceId: string, value: string): void {
  if (!instanceId) return;
  const next = value === ALL ? null : value;
  if (readLocationChoice(instanceId) === next) return;
  cache.set(instanceId, next);
  updateStoredScope(instanceId, { loc: next ?? undefined }, browserStorage());
  notify();
}

/** The remembered raw value for this instance, re-rendering when it changes. */
export function useStoredLocation(instanceId: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => readLocationChoice(instanceId),
    () => null,
  );
}

/** Tests only: forget every cached value so each test starts from storage. */
export function resetLocationStoreForTests(): void {
  cache.clear();
  notify();
}
